import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Grid, Theme, Typography, useMediaQuery } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { mergeWithKey } from 'ramda'
import { Nil, NumberUtils, WellnessPlanVersion } from '@pbt/pbt-ui-components'

import Features from '../../../../constants/features'
import {
  MembershipSignUpFlow,
  WellnessPlanLevels,
} from '../../../../constants/wellnessPlansConstants'
import { getCurrentBusinessIsOmniChannel } from '../../../../store/duck/businesses'
import { getCurrentClient } from '../../../../store/duck/clients'
import { getFeatureToggle } from '../../../../store/duck/constants'
import {
  getMembershipIsPortalEmbed,
  getMembershipSelection,
  getMembershipToken,
  getWellnessPlansIsLoading,
  getWellnessPlansIsSelectionStoring,
  getWellnessPlansVersion,
  updateActiveSignUpFlow,
  updateMembershipSignUpTokenSelection,
  // @ts-ignore
} from '../../../../store/duck/wellnessPlans'
// @ts-ignore
import { useNavigateWithQueryString } from '../../../../utils'
import useBoopEnabled from '../../../../utils/useBoopEnabled'
import useCallbackWhenLoaded from '../../../../utils/useCallbackWhenLoaded'
import KioskLinkButton from '../../../buttons/KioskLinkButton'
import KioskScreen from '../../KioskScreen'
import MembershipTotalsLabel from '../MembershipTotalsLabel'
import useHandleFreeSignUp from '../useHandleFreeSignUp'
import { useKioskMembershipEnrollmentTracking } from '../useKioskMembershipEnrollmentTracking'
import {
  getPlanByLevel,
  useHandleSendSignUpLink,
  useTotalsPerPatient,
  // @ts-ignore
} from '../wellnessPlanUtils'
// @ts-ignore
import MembershipSummaryTableV1 from './MembershipSummaryTableV1'
import { MembershipSummaryTableV2 } from './MembershipSummaryTableV2'

const useStyles = makeStyles(
  (theme) => ({
    title: {
      textAlign: 'left',
      padding: theme.spacing(2, 4, 0),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2, 1, 0),
      },
    },
    content: {
      padding: 0,
    },
    linksContainer: {
      marginRight: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
      },
    },
    baseFeeText: {
      alignSelf: 'flex-start',
      color: theme.colors.tabLabel,
      fontSize: '1.4rem',
      paddingLeft: theme.spacing(4),
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(1),
      },
    },
    confirmButton: {
      fontSize: '1.4rem',
      width: 180,
    },
  }),
  { name: 'MembershipPlanSummaryScreen' },
)

const MembershipPlanSummaryScreen = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation('Membership')
  const location = useLocation()
  const { trackMembershipEnrollment } = useKioskMembershipEnrollmentTracking()

  const navigateWithQueryString = useNavigateWithQueryString()

  const client = useSelector(getCurrentClient)
  const token = useSelector(getMembershipToken)
  const selection = useSelector(getMembershipSelection)
  const isPortalEmbed = useSelector(getMembershipIsPortalEmbed)
  const isMembershipSelectionStoring: boolean | undefined = useSelector(
    getWellnessPlansIsSelectionStoring,
  )
  const isManualSignUpLoading: boolean | undefined = useSelector(
    getWellnessPlansIsLoading,
  )
  const wellnessPlansVersion: WellnessPlanVersion | Nil = useSelector(
    getWellnessPlansVersion,
  )
  const isIpoM2bBraintreeSignupEnabled = useSelector(
    getFeatureToggle(Features.IPO_M2B_BRAINTREE_SIGNUP),
  )

  const isCurrentBusinessOmniChannel = useSelector(
    getCurrentBusinessIsOmniChannel,
  )
  const isSuppressAddClientsAndPatientsEnabled = useSelector(
    getFeatureToggle(Features.SUPPRESS_ADD_CLIENTS_AND_PATIENTS),
  )

  const showAddPet = !(
    isCurrentBusinessOmniChannel && isSuppressAddClientsAndPatientsEnabled
  )
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  const handleSendSignUpLink = useHandleSendSignUpLink()

  const allPlans = wellnessPlansVersion?.plans || []
  const baseLevelPlan = getPlanByLevel(WellnessPlanLevels.BASE, allPlans)
  const baseLevelPlanPrice = NumberUtils.formatMoney(baseLevelPlan?.price)
  const baseLevelPlanPriceType = baseLevelPlan?.priceType?.displayName || ''
  const memberships = client?.memberships || []
  const planOneTimeFeeAmount = NumberUtils.formatMoney(
    wellnessPlansVersion?.oneTimeFeeAmount,
  )
  const boopFlowEnabled = useBoopEnabled()

  React.useEffect(() => {
    trackMembershipEnrollment('PLAN_SUMMARY_VIEWED')
  }, [trackMembershipEnrollment])

  const totalsPerPatient = useTotalsPerPatient(selection, memberships)

  const totals = Object.keys(totalsPerPatient).reduce((acc, patientId) => {
    const totalsForPatient = totalsPerPatient[patientId]
    return mergeWithKey(
      (key, left, right) => left + right,
      acc,
      totalsForPatient,
    )
  }, {})

  const { isLoading: signUpIsLoading } = useHandleFreeSignUp()

  const searchParams = new URLSearchParams(location.search)
  searchParams.delete('view')

  const onSelectionUpdatedForManualFlow = () => {
    navigateWithQueryString({
      url: '/membership/terms-and-conditions',
      search: searchParams.toString()
        ? `?${searchParams.toString()}&view=false`
        : `?view=false`,
    })
  }

  const setCallbackWhenSelectionUpdatedOn = useCallbackWhenLoaded(
    onSelectionUpdatedForManualFlow,
    getWellnessPlansIsLoading,
  )

  const onManualSignUp = () => {
    trackMembershipEnrollment('SUMMARY_CONTINUE_CLICKED')
    dispatch(updateActiveSignUpFlow(MembershipSignUpFlow.MANUAL))

    setCallbackWhenSelectionUpdatedOn()
    dispatch(updateMembershipSignUpTokenSelection(token, selection))
  }

  const onAddPatientRequested = () => {
    navigateWithQueryString({
      url: '/membership/patient-details',
      options: {
        replace: true,
        state: {
          redirectUrl: '/membership/plan-summary',
        },
      },
    })
  }

  const onViewTermsAndConditionsRequested = () => {
    trackMembershipEnrollment('TERMS_LINK_CLICKED')
    navigateWithQueryString({
      url: '/membership/terms-and-conditions',
      search: location.search
        ? `?${searchParams.toString()}&view=true`
        : `?view=true`,
      options: {
        replace: true,
        state: {
          redirectUrl: '/membership/plan-summary',
        },
      },
    })
  }

  const onBeforeBack = () => {
    trackMembershipEnrollment('BACK_CLICKED', {
      stepName: 'plan_summary',
      stepNumber: 5,
    })
  }

  const onSendSignUpLink = () => {
    trackMembershipEnrollment('SUMMARY_CONTINUE_CLICKED')
    handleSendSignUpLink()
  }

  const kioskScreenHybridButtonProps = boopFlowEnabled
    ? {
        onSecondaryButtonClick: onManualSignUp,
        secondaryButtonLabel: 'Manual sign up',
        secondaryButtonDisabled:
          isManualSignUpLoading || isMembershipSelectionStoring,
        proceedButtonLabel: 'Send client a sign-up link',
        proceedButtonDisabled:
          isManualSignUpLoading || isMembershipSelectionStoring,
        proceedButtonLoading:
          isManualSignUpLoading ||
          signUpIsLoading ||
          isMembershipSelectionStoring,
        onProceed: onSendSignUpLink,
      }
    : {
        proceedButtonLabel: 'Manual sign up',
        proceedButtonDisabled:
          isManualSignUpLoading || isMembershipSelectionStoring,
        proceedButtonLoading:
          isManualSignUpLoading ||
          signUpIsLoading ||
          isMembershipSelectionStoring,
        onProceed: onManualSignUp,
      }

  const kioskScreenButtonProps =
    isPortalEmbed && isCurrentBusinessOmniChannel
      ? {
          proceedButtonLabel: 'Continue',
          proceedButtonDisabled: signUpIsLoading || isManualSignUpLoading,
          proceedButtonLoading: signUpIsLoading || isManualSignUpLoading,
          onProceed: onManualSignUp,
        }
      : kioskScreenHybridButtonProps

  return (
    <KioskScreen
      additionalLabel={
        <Grid item xs className={classes.linksContainer} sm="auto">
          {isMobile ? (
            <MembershipTotalsLabel alignItems="center" totals={totals} />
          ) : (
            <>
              {isPortalEmbed && (
                <KioskLinkButton onClick={onViewTermsAndConditionsRequested}>
                  View terms and conditions
                </KioskLinkButton>
              )}
              {showAddPet && (
                <KioskLinkButton onClick={onAddPatientRequested}>
                  Add a pet
                </KioskLinkButton>
              )}
            </>
          )}
        </Grid>
      }
      classes={{
        content: classes.content,
        title: classes.title,
        confirmButton: classes.confirmButton,
      }}
      justifyContent="flex-start"
      title="Plan summary"
      onBeforeBack={onBeforeBack}
      {...kioskScreenButtonProps}
    >
      {baseLevelPlan?.price > 0 && (
        <Typography className={classes.baseFeeText}>
          {wellnessPlansVersion?.oneTimeFeeAmount
            ? t('Membership:EACH_SIGN_UP_HAS_BASE_AND_OTF_FEE_OF', {
                planPrice: `${baseLevelPlanPrice}/${baseLevelPlanPriceType}`,
                planOneTimeFeeAmount,
              })
            : t('Membership:EACH_SIGN_UP_HAS_BASE_FEE_OF', {
                planPrice: `${baseLevelPlanPrice}/${baseLevelPlanPriceType}`,
              })}
        </Typography>
      )}
      {isIpoM2bBraintreeSignupEnabled && isCurrentBusinessOmniChannel ? (
        <MembershipSummaryTableV2
          totals={totals}
          totalsPerPatient={totalsPerPatient}
        />
      ) : (
        <MembershipSummaryTableV1
          totals={totals}
          totalsPerPatient={totalsPerPatient}
        />
      )}
    </KioskScreen>
  )
}

export default MembershipPlanSummaryScreen
